import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { fetchAdminUsers } from "@admin/services/adminApi";
import { PageHeader, DataTable, Pagination, FilterBar, Pill } from "@admin/components/Primitives";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const LIMIT = 25;

export default function AdminUsers() {
  const [data, setData] = useState({ total: 0, items: [] });
  const [skip, setSkip] = useState(0);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchAdminUsers({ q: q || undefined, role, skip, limit: LIMIT });
      setData(r);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load users"); }
    setLoading(false);
  }, [q, role, skip]);

  useEffect(() => { load(); }, [load]);

  const columns = [
    {
      key: "name",
      label: "User",
      render: (r) => (
        <div className="min-w-[220px]">
          <p className="font-bold text-[#0A2540] text-sm">{r.name || "—"}</p>
          <p className="text-[11px] text-slate-500">{r.email || r.phone || "—"}</p>
        </div>
      ),
    },
    {
      key: "role",
      label: "Role",
      render: (r) => (
        <Pill color={r.role === "admin" ? "gold" : r.role === "lawyer" ? "blue" : "slate"}>{r.role}</Pill>
      ),
    },
    {
      key: "permissions",
      label: "Perms",
      render: (r) => <span className="text-xs text-slate-500">{(r.permissions || []).length}</span>,
    },
    {
      key: "created_at",
      label: "Joined",
      render: (r) => <span className="text-xs text-slate-500">{(r.created_at || "").slice(0, 10)}</span>,
    },
  ];

  return (
    <>
      <PageHeader testid="admin-users-header" title="Users" subtitle="View all users — clients, lawyers, and admins." />
      <FilterBar testid="users-filterbar">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => { setSkip(0); setQ(e.target.value); }} placeholder="Search by name, email, phone…" className="pl-8" data-testid="users-search" />
        </div>
        <Select value={role} onValueChange={(v) => { setSkip(0); setRole(v); }}>
          <SelectTrigger className="w-[140px]" data-testid="users-filter-role"><SelectValue placeholder="Role" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="user">User</SelectItem>
            <SelectItem value="lawyer">Lawyer</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
          </SelectContent>
        </Select>
      </FilterBar>
      <DataTable testid="users-table" columns={columns} rows={data.items} empty={loading ? "Loading…" : "No users match"} />
      <Pagination testid="users-pagination" total={data.total} skip={skip} limit={LIMIT} onChange={setSkip} />
    </>
  );
}
